import { CheckBadgeIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { updatePassword } from 'services'
import { Input2, Modal } from 'stories/components'

interface InputStates {
  oldPassword: string
  password: string
  confirm: string
}

interface ErrorStates {
  oldPassword: string
  password: string
  confirm: string
}

const defaultInputs: InputStates = {
  oldPassword: '',
  password: '',
  confirm: '',
}

export const ResetPasswordModal = ({
  isOpen,
  onClose,
  email,
}: {
  isOpen: boolean
  onClose: (completed?: boolean) => void
  email: string
}) => {
  const [loading, setLoading] = useState(false)
  const [inputStates, setInputStates] = useState<InputStates>(defaultInputs)
  const [submitted, setSubmitted] = useState(false)
  const completedRef = useRef(false)
  const [errors, setErrors] = useState<ErrorStates>({
    oldPassword: '',
    password: '',
    confirm: '',
  })

  const auth = useSelector((state: any) => state.auth)

  const validatePassword = () => {
    const newErrors: ErrorStates = {
      oldPassword: '',
      password: '',
      confirm: '',
    }
    if (!inputStates.oldPassword) newErrors.oldPassword = 'Required'
    if (!inputStates.password) newErrors.password = 'Required'
    else if (inputStates.password.length < 6) newErrors.password = 'Must be at least 6 characters'
    else if (!inputStates.confirm) newErrors.confirm = 'Required'
    else if (inputStates.password !== inputStates.confirm) newErrors.confirm = 'Not same as New Password'
    return newErrors
  }

  const onSubmit = async () => {
    const newErrors = validatePassword()
    if (newErrors.oldPassword || newErrors.password || newErrors.confirm) {
      setErrors(newErrors)
      return
    }

    if (auth.isAuthenticated && (auth.profile?.requireReset || auth.requirePwChange)) {
      setLoading(true)
      updatePassword(email, inputStates.password, inputStates.oldPassword)
        .then((res) => {
          if (res.success !== false) {
            completedRef.current = true
            setSubmitted(true)
            setTimeout(() => {
              handleClose()
            }, 1500)
          }
        })
        .finally(() => setLoading(false))
    }
  }

  const onChange = (key: keyof InputStates, value: string) => {
    setInputStates((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const handleClose = () => {
    const completed = completedRef.current
    completedRef.current = false
    setInputStates(defaultInputs)
    setErrors({
      oldPassword: '',
      password: '',
      confirm: '',
    })
    setSubmitted(false)
    onClose(completed)
  }

  if (!isOpen) return null

  return (
    <form>
      <Modal
        title="Change Password"
        titleOkay={submitted ? 'Close' : 'Update'}
        titleCancel=""
        hiddenX
        loading={loading}
        isOpen={isOpen}
        lastUpdatedAt={Date.now()}
        onClose={handleClose}
        onOk={submitted ? handleClose : onSubmit}
      >
        {submitted ? (
          <div className="w-96">
            <div
              className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative mb-4 text-[15.5px]"
              role="alert"
            >
              <div className="flex justify-center mb-3">
                <CheckBadgeIcon className="w-10 h-10" />
              </div>
              <div className="max-w-xl">Password updated successfully!</div>
            </div>
          </div>
        ) : (
          <div className="w-96">
            <Input2
              type="password"
              title="Old Password"
              value={inputStates.oldPassword}
              required
              error={errors.oldPassword}
              onChange={(value) => onChange('oldPassword', value)}
            />
            <div className="my-4" />
            <Input2
              type="password"
              title="New Password"
              value={inputStates.password}
              required
              error={errors.password}
              onChange={(value) => onChange('password', value)}
            />
            <div className="my-4" />
            <Input2
              type="password"
              title="Confirm Password"
              value={inputStates.confirm}
              required
              error={errors.confirm}
              onChange={(value) => onChange('confirm', value)}
            />
          </div>
        )}
      </Modal>
    </form>
  )
}
